/**
 * O cartão do site para o primeiro link de um texto (27/10/2026).
 *
 * Cada tela entrega o `PrimeiroLink` (`lib/links`) e a origem da sessão; o hook decide sozinho se vai
 * ao servidor. A consulta passa pelo lote (`consultaEmLote`): 30 cartões montados juntos viram um ou
 * três pedidos, não 30. O cache é do react-query: 5 min de frescor, 30 min na memória, sem retry.
 *
 * 🔴 Só vai à rede o que o servidor aceita. Um link recusado dá 422 ao pedido INTEIRO e todos os
 * cartões da tela somem (Review Focus 2); por isso o hook só consulta com `normalizado` não nulo
 * (<= 2048 caracteres; o ASCII vale só na autoridade, isto é, usuário, senha e host; o caminho e a
 * consulta podem ter acento), fora da casa (o cartão do próprio SE_WEB seria inútil e vazaria o
 * caminho) e sem usuário e senha. E `habilitado: false` (o interruptor desligado, a tela em modo de
 * edição) é respeitado ao pé da letra, com o cache e o pedido em voo ignorados: o cartão some e o
 * `carregando` cai na hora.
 *
 * A falha vira "sem cartão" e fica calada (sem toast): o link continua clicável e o cartão é só um
 * enfeite. A chave depende de `normalizado`, não da identidade do objeto `PrimeiroLink` (que as telas
 * recriam a cada digitação).
 *
 * No portal o parceiro não tem o GET do interruptor; o `ligada` de cada resposta é gravado em
 * `CHAVE_LIGADA_DO_PORTAL` e é por ele que `useCartoesLigados("portal")` responde.
 */
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";

import { consultaDaOrigem } from "@/lib/links/consultaEmLote";
import type { PrimeiroLink } from "@/lib/links/links";
import type { CartaoDoSite, OrigemDasPrevias } from "@/services/previasDeLinkApi";

import { CHAVE_LIGADA_DO_PORTAL } from "./useCartoesLigados";

export function usePreviaDeLink(
  link: PrimeiroLink | null,
  origem: OrigemDasPrevias,
  opcoes?: { habilitado?: boolean }
): { cartao: CartaoDoSite | null; carregando: boolean } {
  const queryClient = useQueryClient();
  const normalizado = link?.normalizado ?? null;
  const habilitado =
    link !== null &&
    normalizado !== null &&
    !link.casa &&
    !link.comCredencial &&
    opcoes?.habilitado !== false;

  const consulta = useQuery<CartaoDoSite | null>({
    queryKey: ["previa-de-link", origem, normalizado],
    // Sem endereço não há função de busca (`skipToken`): além do `enabled`, é o cinto que garante que
    // `null` nunca chega ao servidor.
    queryFn:
      normalizado === null
        ? skipToken
        : async () => {
            const resultado = await consultaDaOrigem(origem).consultar(normalizado);
            if (origem === "portal")
              queryClient.setQueryData(CHAVE_LIGADA_DO_PORTAL, resultado.ligada);
            return resultado.cartao;
          },
    enabled: habilitado,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    // Desabilitado, o cartão que o cache ainda guarda não aparece, e o pedido que ainda estava em voo
    // (o observer segue em `isLoading` até a resposta chegar) não conta como carregando.
    cartao: habilitado ? (consulta.data ?? null) : null,
    carregando: habilitado && consulta.isLoading,
  };
}
